import {
  CRMChannel,
  PlanTierIndex,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { DataLoadingWrapper } from "@jugl-web/ui-components";
import { Avatar } from "@jugl-web/ui-kit";
import { FC, useMemo } from "react";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { getContactChannelConnectionStatus } from "@web-src/modules/contacts/ContactsPage/components/ContactIntelligenceInfoSection/utils";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { getPlanTierIndex } from "@web-src/modules/cx/pages/CXPlansPage/utils/planTierUtils";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { ReactComponent as UserIcon } from "./assets/user.svg";
import { getConversationUsername } from "../../../WebChatAvatar/utils";
import { getEmailConversationContactName } from "../../../../utils/getEmailConversationContactName";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useClientSupportPageWithSelectedConversation } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageWithSelectedConversation";
import { CustomerActionsMenu } from "../../../ClientSupportChat/components/CustomerActionsMenu";
import { CustomerProfileSections } from "../../../CustomerInfo";
import { EmailAvatar } from "../../../EmailAvatar";
import { ReactComponent as InfoIcon } from "./assets/info.svg";

export const CustomerTabContent: FC = () => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { currentPlan } = useSubscriptionV2();
  const { isContactBlocked } = useClientSupportPageContext();
  const { selectedConversation, selectedChannel } =
    useClientSupportPageWithSelectedConversation();
  const { isCx } = useAppVariant();

  const {
    data: crmConfig,
    isLoading: isCrmConfigLoading,
    isError: isCrmConfigError,
    refetch: refetchCrmConfig,
  } = integrationsApi.useGetCRMConfigQuery({ entityId });

  const contact = selectedConversation.contact_info;

  const channelConnectionStatus = useMemo(
    () =>
      crmConfig ? getContactChannelConnectionStatus(crmConfig) : undefined,
    [crmConfig]
  );

  const hasBusinessPlanOrAbove = useMemo(
    () =>
      currentPlan && getPlanTierIndex(currentPlan) >= PlanTierIndex.business,
    [currentPlan]
  );

  const isWebChannel = selectedChannel === CRMChannel.chatbot;
  const isEmailChannel = selectedChannel === CRMChannel.email;
  const isTwilioChannel = selectedChannel === CRMChannel.twilio;

  const { contactName, avatarUsername } = useMemo(() => {
    if (isEmailChannel) {
      const displayName =
        contact?.name ||
        getEmailConversationContactName(selectedConversation, {
          withEmailFallback: false,
        });

      return {
        contactName: displayName,
        avatarUsername: displayName,
      };
    }

    const displayName = contact?.name || selectedConversation?.name || "";

    return {
      contactName: displayName,
      avatarUsername: isWebChannel
        ? getConversationUsername(displayName)
        : displayName,
    };
  }, [contact?.name, isEmailChannel, isWebChannel, selectedConversation]);

  return (
    <div className="py-10">
      <div className="flex shrink items-center gap-4 px-6">
        {isTwilioChannel ? (
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full"
            style={{
              background: "linear-gradient(180deg, #CECED3 0%, #ECEFF0 69.27%)",
            }}
          >
            <UserIcon />
          </div>
        ) : isEmailChannel ? (
          <EmailAvatar className="h-14 w-14" username={avatarUsername} />
        ) : (
          <Avatar size="md" username={avatarUsername} />
        )}
        <div className="flex grow flex-col gap-1 truncate">
          <span className="truncate font-secondary text-lg leading-[140%] font-semibold tracking-[0.18px] text-dark">
            {contactName}
          </span>
          {isContactBlocked && (
            <div className="flex items-center gap-1.5">
              <InfoIcon />
              <span className="font-secondary text-xs leading-[140%] tracking-[0.12px] text-dark-700">
                {t({
                  id: "client-support-chat-page.account-is-blocked",
                  defaultMessage: "Account is Blocked",
                })}
              </span>
            </div>
          )}
        </div>
        {selectedChannel === CRMChannel.whatsapp && <CustomerActionsMenu />}
      </div>
      <div className="my-7 h-px w-full shrink-0 bg-[#F2F2F2]" />
      <DataLoadingWrapper
        isLoading={isCrmConfigLoading}
        isError={isCrmConfigError}
        onRetry={refetchCrmConfig}
      >
        <CustomerProfileSections
          contact={contact ?? {}}
          channelConnectionStatus={channelConnectionStatus}
          isVisitorIntelligenceVisible={
            !!hasBusinessPlanOrAbove && isWebChannel && isCx
          }
        />
      </DataLoadingWrapper>
    </div>
  );
};
